import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TenderMethodService } from 'src/app/core/services/general/tender-method.service';

/**
 * "How was this paid?" — the tender method, asked where money actually changes hands.
 *
 * ── Why it is asked at all ───────────────────────────────────────────────────────────────────
 * The end-of-day cash-up can only localise a discrepancy if it knows how the money arrived: cash
 * missing from a till and a transfer that never landed are different problems with the same symptom.
 * Nothing in this system recorded it before Stage 2, so every receipt to date reads UNRECORDED.
 *
 * ── Deliberately NOT like the currency picker in two ways ────────────────────────────────────
 * 1. **It asks whether or not multi-currency is on.** How money arrived has nothing to do with which
 *    book it is in; a single-currency terminal still needs its drawer counted.
 * 2. **It pre-picks NOTHING.** The currency picker defaults to the base because a charge must land in
 *    some book. A method has no safe default: defaulting to CASH would quietly assert that money is in
 *    a drawer, which is the exact claim a cash-up exists to check. The operator takes the money; the
 *    operator says how.
 *
 * ── And it renders nothing when no money is moving ───────────────────────────────────────────
 * A gate pass releases cargo ON ACCOUNT. There is no tender, so there is no question — and the API
 * records none against a voucher however the screen is driven.
 */
@Component({
  selector: 'app-tender-method-choice',
  templateUrl: './tender-method-choice.component.html',
  styleUrls: ['./tender-method-choice.component.scss'],
})
export class TenderMethodChoiceComponent implements OnInit {
  /** What is being paid, in the operator's words: "parking charge", "examination fee", "payment". */
  @Input() what = 'payment';

  /** False when nothing is being tendered (a gate pass), so no question is asked. */
  @Input() asking = true;

  /** Two-way: the method the operator picked, or null until they do. */
  @Input() value: string | null | undefined = null;
  @Output() valueChange = new EventEmitter<string | null>();

  @Input() disabled = false;

  constructor(private tenderMethods: TenderMethodService) {}

  ngOnInit(): void {
    this.tenderMethods.load();
  }

  /**
   * Which group the operator has opened, when it has more than one method inside it. Null = the first
   * question is still on screen.
   */
  public openGroup: string | null = null;

  /** Nothing to ask when no money is moving, and nothing to show before the list has loaded. */
  get asks(): boolean {
    return this.asking && this.groups.length > 0;
  }

  /** The first question: "is it cash, or swiped, or mobile money?" */
  get groups(): Array<string> {
    return Object.keys(this.tenderMethods.groups);
  }

  /** The methods inside a group. A group of one is just a button. */
  methodsIn(group: string): Array<string> {
    return this.tenderMethods.groups[group] ?? [];
  }

  /** Does this group need a second question, or is it a single method? */
  opens(group: string): boolean {
    return this.methodsIn(group).length > 1;
  }

  /** The second question's options, once a group is open. */
  get openChoices(): Array<string> {
    return this.openGroup === null ? [] : this.methodsIn(this.openGroup);
  }

  /** Every storable method — what a caller validates against. */
  get choices(): Array<string> {
    return this.tenderMethods.methods;
  }

  /** The group a picked method belongs to, so the screen can show where it came from. */
  get pickedGroup(): string | null {
    const picked = (this.value ?? '').trim().toUpperCase();
    if (picked === '') {
      return null;
    }

    return this.groups.find((g) => this.methodsIn(g).some((m) => m.toUpperCase() === picked)) ?? null;
  }

  /** A group whose single method is the one picked — so CASH highlights without opening anything. */
  isGroupPicked(group: string): boolean {
    return !this.opens(group) && this.isPicked(this.methodsIn(group)[0] ?? '');
  }

  logosFor(method: string): Array<string> {
    return this.tenderMethods.logosFor(method);
  }

  iconFor(method: string): string {
    return this.tenderMethods.iconFor(method);
  }

  /**
   * The first question. A group of one picks straight through — a cashier should not have to tap
   * "CASH" and then "CASH". A group of several opens its second question instead.
   */
  pickGroup(group: string): void {
    if (this.disabled) {
      return;
    }

    if (this.opens(group)) {
      this.openGroup = group;

      return;
    }

    this.openGroup = null;
    this.pick(this.methodsIn(group)[0] ?? '');
  }

  /** Back out of a group without having answered, so a mis-tap is one tap to undo. */
  closeGroup(): void {
    this.openGroup = null;
  }

  /** Has the operator answered? The dialogs use this to keep Confirm disabled until they have. */
  get answered(): boolean {
    return (this.value ?? '').trim() !== '';
  }

  pick(method: string): void {
    if (this.disabled) {
      return;
    }
    this.openGroup = null;
    this.value = method;
    this.valueChange.emit(method);
  }

  isPicked(method: string): boolean {
    return (this.value ?? '').trim().toUpperCase() === method.trim().toUpperCase();
  }
}
